<template>
  <div class="AsidePrices" :class="`AsidePrices--align-${align}`">
    <ai-tags v-if="tags?.length" :items="tags" />

    <ai-typo
      v-if="description"
      class="AsidePrices-description"
      as="p"
      variant="paragraph-04-bold">
      {{ description }}
    </ai-typo>

    <ai-typo
      v-if="pricesPrefixText"
      class="AsidePrices-prefix"
      as="p"
      variant="overline-01"
      color="stratos-800">
      {{ pricesPrefixText }}
    </ai-typo>

    <div v-if="isMember && memberPrice" class="AsidePrices-loggedIn">
      <div class="AsidePrices-price AsidePrices-price--memberLoggedIn">
        <ai-amount
          main-typo="paragraph-01-bold"
          decimal-typo="paragraph-03-bold"
          :amount="convertedMemberPrice.amount"
          :symbol="convertedMemberPrice.currency?.code"
          :member-tag="memberTag"
          member-tag-mobile-inverse
          member-tag-top-offset="8%" />
      </div>
      <div
        v-if="convertedPublicPrice.amount"
        class="AsidePrices-price AsidePrices-price--publicLoggedIn">
        <ai-amount
          :amount="convertedPublicPrice.amount"
          main-typo="paragraph-03-regular"
          decimal-typo="paragraph-03-regular"
          :symbol="convertedPublicPrice.currency?.code"
          strike-through />
      </div>
    </div>

    <div v-else-if="isMember && !memberPrice">
      <div class="AsidePrices-price AsidePrices-price--memberLoggedIn">
        <ai-amount
          main-typo="paragraph-01-bold"
          decimal-typo="paragraph-03-bold"
          :amount="convertedPublicPrice.amount"
          :symbol="convertedPublicPrice.currency?.code" />
      </div>
    </div>

    <div v-else class="AsidePrices-table" role="table">
      <div class="AsidePrices-price AsidePrices-price--public" role="row">
        <ai-typo
          v-if="memberPrice"
          as="span"
          variant="overline-01"
          class="AsidePrices-rates"
          role="cell">
          {{ $t('booking.roomRate.publicLabel') }}
        </ai-typo>
        <div class="AsidePrices-priceValues" role="cell">
          <ai-amount
            v-if="showDiscount"
            main-typo="paragraph-03-regular"
            decimal-typo="paragraph-03-regular"
            :amount="originalPublicPrice"
            :symbol="convertedPublicPrice.currency?.code"
            strike-through />
          <ai-amount
            :main-typo="
              memberPrice ? 'paragraph-01-regular' : 'paragraph-01-bold'
            "
            :decimal-typo="
              memberPrice ? 'paragraph-03-regular' : 'paragraph-03-bold'
            "
            :amount="convertedPublicPrice.amount"
            :symbol="convertedPublicPrice.currency?.code" />
        </div>
      </div>
      <div
        v-if="memberPrice"
        class="AsidePrices-price AsidePrices-price--member"
        role="row">
        <ai-typo
          as="span"
          variant="overline-01"
          class="AsidePrices-rates"
          role="cell">
          {{ $t('booking.roomRate.memberLabel') }}
        </ai-typo>
        <div class="AsidePrices-priceValues" role="cell">
          <ai-amount
            v-if="showDiscount"
            main-typo="paragraph-03-regular"
            decimal-typo="paragraph-03-regular"
            :amount="originalMemberPrice"
            :symbol="convertedMemberPrice.currency?.code"
            strike-through />
          <ai-amount
            main-typo="paragraph-01-bold"
            decimal-typo="paragraph-03-bold"
            :amount="convertedMemberPrice.amount"
            :symbol="convertedMemberPrice.currency?.code" />
        </div>
      </div>
    </div>

    <ai-typo
      v-if="taxLabel"
      class="AsidePrices-tax"
      :class="{ 'AsidePrices-tax--connect': isMember && memberPrice }"
      as="span"
      variant="paragraph-04-regular">
      {{ taxLabel }}
    </ai-typo>
  </div>
</template>

<script setup lang="ts">
import type { AsidePricesProps } from './types';

const props = withDefaults(defineProps<AsidePricesProps>(), {
  tags: () => [],
  description: undefined,
  pricesPrefixText: undefined,
  currency: null,
  memberPrice: null,
  publicPrice: null,
  userCurrency: undefined,
  taxLabel: undefined,
  discount: undefined,
  memberTag: 'left',
  align: 'right',
});

const hotelCurrency = computed(() => props.currency);
const targetCurrency = computed(() => props.userCurrency);

const convertedPublicPrice = useConvertToUserCurrency(
  props.publicPrice || 0,
  hotelCurrency,
  targetCurrency,
);

const convertedMemberPrice = useConvertToUserCurrency(
  props.memberPrice || 0,
  hotelCurrency,
  targetCurrency,
);

const showDiscount = computed(
  () =>
    !props.isMember &&
    typeof props.discount === 'number' &&
    props.discount > 0 &&
    props.discount < 100,
);

const originalPublicPrice = computed(() => {
  if (!showDiscount.value) {
    return null;
  }

  return convertedPublicPrice.value.amount / (1 - props.discount! / 100);
});

const originalMemberPrice = computed(() => {
  if (!showDiscount.value || !props.memberPrice) {
    return null;
  }

  return convertedMemberPrice.value.amount / (1 - props.discount! / 100);
});
</script>

<style lang="scss" scoped>
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mq';

.AsidePrices {
  display: flex;
  flex-direction: column;

  &--align-left {
    align-items: flex-start;

    .AsidePrices-table {
      align-items: flex-start;
    }
  }

  &--align-right {
    align-items: flex-end;

    .AsidePrices-table {
      align-items: flex-end;
    }

    .AsidePrices-tax {
      text-align: right;
    }
  }
}

.AsidePrices-description {
  margin-bottom: constants.$margin-00;
  text-align: left;
  width: 100%;
}

.AsidePrices-prefix {
  margin-bottom: constants.$margin-00;
  width: 100%;
  text-align: left;
}

.AsidePrices--align-right .AsidePrices-prefix {
  text-align: right;
}

.AsidePrices-table {
  display: flex;
  flex-direction: column;
}

.AsidePrices-rates {
  text-transform: uppercase;
  margin-right: constants.$inner-02;
  margin-bottom: constants.$inner-01;

  @media (mq.$from-medium) {
    margin-bottom: constants.$inner-01;
  }
}

.AsidePrices-price {
  position: relative;
  margin-bottom: constants.$margin-01;
  width: max-content;
  display: flex;
  align-items: baseline;

  &--public {
    margin-bottom: constants.$inner-00;
  }

  &--member {
    margin-bottom: constants.$margin-01;
    background-color: colors.$gold-100;
  }

  &--memberLoggedIn {
    padding-top: 0;
    padding-bottom: 0;
    margin-bottom: 0;
  }

  &--publicLoggedIn {
    padding-top: 0;
    padding-bottom: 0;
    margin-bottom: constants.$margin-01;
  }
}

.AsidePrices-priceValues {
  display: inline-flex;
  align-items: baseline;
  gap: constants.$inner-02;
}

.AsidePrices-tax {
  color: colors.$stratos-700;
  width: 100%;
  text-align: left;

  &--connect {
    margin-top: 0;
  }
}

.theme.rixos .AsidePrices-price--member {
  background-color: colors.$white;
}
</style>
